// Types + pure helpers for the prize-product editor. Browser-side mirror of the
// server contract in ../../../lib/prize-product.js (copy-not-import, per house
// convention). Coordinate math converts between the mockup image's NATIVE pixels
// (what gets stored + what proof-email-function composites against) and on-screen
// displayed pixels.

import { MAX_PRIZE_TIERS, prizeIndexes } from './prize-tiers';

export function isPrizeTier(t: unknown): t is number {
  return Number.isInteger(t) && (t as number) >= 1 && (t as number) <= MAX_PRIZE_TIERS;
}

export type LogoPosition = { x: number; y: number };

export type PrizeProductMockup = {
  baseImage: string;
  overlayImage?: string;
  logoPosition: LogoPosition;
  logoMaxWidth: number;
  logoMaxHeight: number;
};

export type PrizeProductShopify = {
  titlePrefix?: string; skuPrefix?: string; skuSuffix?: string;
  sizes: string[]; basePrice?: string; compareAtPrice?: string | null;
  productType?: string; vendor?: string; tags?: string[];
  trackInventory?: boolean; taxable?: boolean; publishedScope?: string;
  bodyHtml?: string; metafields?: unknown[];
};

// --- decoration registry (platformConfig/decorationLocations via
// GET /api/decoration-locations) + per-product print areas.
// Mirrors ../../../lib/decoration-locations.js and
// productconfig/lib/decoration.js. There is no hardcoded location map on this
// side any more: codes, labels and the legacy placement aliases are all data.

// THE data vocabularies. Both lists mirror ../../../lib/decoration-locations.js
// VALID_KINDS / VALID_VIEWS, and every other module on this side imports them
// from here rather than re-typing the literals — a second copy that drifts is a
// filter that silently stops matching (Codex 2026-08-11: the kind list and the
// view list were each written out twice).
export type DecorationKind = 'logo' | 'name' | 'number' | 'name-number';
export const VALID_KINDS: DecorationKind[] = ['logo', 'name', 'number', 'name-number'];
export type MockupViewName = 'front' | 'back' | 'sleeve-left' | 'sleeve-right';
export const VALID_VIEWS: MockupViewName[] = ['front', 'back', 'sleeve-left', 'sleeve-right'];

/** The one human label per garment side. Tab titles and prose both read this. */
export const VIEW_LABELS: Record<MockupViewName, string> = {
  front: 'Front',
  back: 'Back',
  'sleeve-left': 'Left Sleeve',
  'sleeve-right': 'Right Sleeve',
};

// `standard: true` marks the four house-standard print zones (FF Full Front,
// LC Left Chest, RC Right Chest, UB Upper Back — Grant 2026-08-11). It is a
// registry flag, not a client constant: the editor offers standard codes first
// but never hardcodes which codes those are.
// `conflictsWith` (Grant 2026-08-11) lists the codes a location may not share a
// SUBMISSION with — FF against LC/RC, symmetrically. A product may still OFFER
// both sides; the exclusion happens when someone orders. Nothing on this side
// hardcodes which codes those are.
// `stacksWith` names the ONE location this one forms a name-over-number STACK
// with (BN <-> BC today). It is a DECLARATION, never something a consumer may
// re-derive from view + kinds: a garment that gains a second number location
// would grow a pair nobody declared, and this console would then disagree with
// the admin form and the customer edit form about what "stacked" means. The
// field is on the wire already — GET /api/decoration-locations serves
// `registry.locations` verbatim — so carrying it through this type is the whole
// fix. Symmetric by validation server-side; read as mutual here anyway.
export type DecorationLocation = {
  code: string; label: string; view: MockupViewName; kinds: DecorationKind[];
  sort: number; active?: boolean; standard?: boolean; conflictsWith?: string[];
  stacksWith?: string | null;
  placementAlias?: string;
};
export type DecorationRegistry = { locations: Record<string, DecorationLocation> };

export type PrintAreaMockup = { view: MockupViewName; x: number; y: number; maxW: number; maxH: number };
// No `arch`: text renders straight (product decision 2026-07-09) and the
// server rejects the key.
export type PrintArea = {
  widthInches?: number; heightInches?: number;
  mockup?: PrintAreaMockup;
  font?: string; maxChars?: number; maxDigits?: number; ink?: string;
};
export type MockupView = { baseImage: string; overlayImage?: string };

// Prize name/number catalog (Grant 2026-08-13). One font + one ink per slot.
// `pro-block` is unused on prize products and must not appear in pickers —
// a free-text value the renderer would 400 is refused at save.
export type PrizeFontId = 'athletic-block' | 'athletic-arched' | 'script' | 'modern' | 'zing-rust' | 'zeroathletics';
export const PRIZE_FONTS: ReadonlyArray<{ id: PrizeFontId; label: string }> = [
  { id: 'athletic-block', label: 'Athletic Block' },
  { id: 'athletic-arched', label: 'Athletic Arched' },
  { id: 'script', label: 'Script' },
  { id: 'modern', label: 'Modern' },
  { id: 'zing-rust', label: 'Zing Rust' },
  { id: 'zeroathletics', label: 'Zero Athletics' },
];
export const PRIZE_FONT_IDS: readonly PrizeFontId[] = PRIZE_FONTS.map((f) => f.id);
export const DEFAULT_PRIZE_FONT: PrizeFontId = 'athletic-block';
export const DEFAULT_PRIZE_INK = '#FFFFFF';
export const PRIZE_INK_RE = /^#[0-9a-fA-F]{6}$/;
// Name / number / name-and-number ink is White or Black only. The layer
// builder already stores hex (`#FFFFFF` / `#000000`) — do not invent a
// third encoding (`white` / `black`). Product print-area defaults may still
// be any hex; prize-slot pickers snap anything else to white.
export const PRIZE_SLOT_INK_WHITE = '#FFFFFF';
export const PRIZE_SLOT_INK_BLACK = '#000000';
export const PRIZE_SLOT_INK_CHOICES = Object.freeze([
  { value: PRIZE_SLOT_INK_WHITE, label: 'White' },
  { value: PRIZE_SLOT_INK_BLACK, label: 'Black' },
]);
export type PrizeSlotInk = typeof PRIZE_SLOT_INK_WHITE | typeof PRIZE_SLOT_INK_BLACK;

export function isPrizeFont(font: string | undefined | null): font is PrizeFontId {
  return !!font && (PRIZE_FONT_IDS as readonly string[]).includes(font);
}

export function isTextFillKind(kind: string | undefined | null): boolean {
  return kind === 'name' || kind === 'number' || kind === 'name-number';
}

export function resolvePrizeFont(raw: string | undefined | null): PrizeFontId {
  return isPrizeFont(raw) ? raw : DEFAULT_PRIZE_FONT;
}

export function resolvePrizeInk(raw: string | undefined | null): string {
  return typeof raw === 'string' && PRIZE_INK_RE.test(raw) ? raw.toUpperCase() : DEFAULT_PRIZE_INK;
}

export function isPrizeSlotInk(raw: string | undefined | null): raw is PrizeSlotInk {
  const hex = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return hex === PRIZE_SLOT_INK_WHITE || hex === PRIZE_SLOT_INK_BLACK;
}

export function resolvePrizeSlotInk(raw: string | undefined | null): PrizeSlotInk {
  return isPrizeSlotInk(raw) ? (raw.trim().toUpperCase() as PrizeSlotInk) : PRIZE_SLOT_INK_WHITE;
}

export type PrizeProduct = {
  // Firestore's updateTime for this document, served by GET /api/prize-products
  // and demanded back by PUT /api/prize-products/:id before it replaces the
  // document. It is METADATA, never a stored field — the server strips it again
  // on write. Declared here so the round-trip is deliberate and typed rather
  // than an accident of structuredClone: without it, two operators editing the
  // same product silently overwrite each other's zones (../../../lib/prize-product.js).
  _rev?: string;
  id: string; displayName: string; shortName?: string;
  tier: number; product_index?: number; tiers?: number[]; sortOrder?: number; active?: boolean;
  garment?: string; color?: string; placement?: string;
  sku?: { styleCode?: string; colorCode?: string };
  mockup: PrizeProductMockup;
  production?: { width?: number; height?: number; unit?: string; dpi?: number };
  printAreas?: Record<string, PrintArea>;
  mockupViews?: Record<string, MockupView>;
  shopify: PrizeProductShopify;
};

export type Zone = { x: number; y: number; logoMaxWidth: number; logoMaxHeight: number };

export function resolveLocation(reg: DecorationRegistry, code: string): DecorationLocation {
  const loc = reg?.locations?.[code];
  if (!loc) throw new Error(`Unknown decoration location code: ${code}`);
  return loc;
}

export function sortedLocations(reg: DecorationRegistry | undefined): DecorationLocation[] {
  return Object.values(reg?.locations || {})
    .filter((l) => l.active !== false)
    .sort((a, b) => a.sort - b.sort);
}

export const isTextCapable = (loc: DecorationLocation) => loc.kinds.includes('name') || loc.kinds.includes('number');

// --- zone conflicts. Browser mirror of ../../../lib/decoration-locations.js
// conflictsFor / conflictingPairs (copy-not-import, per house convention).

/** The codes `code` may not share a submission with. Throws on an unknown code. */
export function conflictsFor(reg: DecorationRegistry, code: string): string[] {
  const loc = resolveLocation(reg, code);
  return Array.isArray(loc.conflictsWith) ? loc.conflictsWith.slice() : [];
}

/**
 * Every conflicting pair present in `codes`, as [a, b] in the order given.
 * A pair counts when EITHER side declares it: the server refuses an asymmetric
 * registry at boot, but this side is handed whatever the API returned, and
 * answering "no conflict" because one half is missing would silently switch the
 * rule off in the UI.
 */
export function conflictingPairs(reg: DecorationRegistry, codes: string[]): [string, string][] {
  const list: string[] = [];
  for (const code of codes || []) if (!list.includes(code)) list.push(code);
  const pairs: [string, string][] = [];
  for (let i = 0; i < list.length; i += 1) {
    const a = conflictsFor(reg, list[i]);
    for (let j = i + 1; j < list.length; j += 1) {
      const b = conflictsFor(reg, list[j]);
      if (a.includes(list[j]) || b.includes(list[i])) pairs.push([list[i], list[j]]);
    }
  }
  return pairs;
}

// --- name-over-number stacks. Browser mirror of
// ../../../lib/decoration-locations.js stackPartnerFor and of the mutuality test
// requestedit/lib/slot-edit.js stackedNameNumberPreset applies
// (copy-not-import, per house convention).

/** The location `code` declares a stack with, or null. Throws on unknown code. */
export function stackPartnerFor(reg: DecorationRegistry, code: string): string | null {
  const loc = resolveLocation(reg, code);
  return typeof loc.stacksWith === 'string' && loc.stacksWith ? loc.stacksWith : null;
}

/**
 * Do `a` and `b` form a declared stack pair?
 *
 * BOTH halves must name each other. The server refuses an asymmetric registry at
 * boot, but this side is handed whatever the API returned, and honouring a
 * half-declared pair would label a stack the other two forms do not offer —
 * requestedit makes exactly this check ("a half-declared pair is NOT a pair").
 * Unknown codes answer false rather than throwing: the caller is rendering a
 * card that already flags itself as an unrecognized location, and a throw would
 * blank the panel instead of showing that.
 */
export function isDeclaredStackPair(
  reg: DecorationRegistry | undefined,
  a: string,
  b: string,
): boolean {
  if (!reg?.locations || !a || !b || a === b) return false;
  const la = reg.locations[a];
  const lb = reg.locations[b];
  if (!la || !lb) return false;
  return la.stacksWith === b && lb.stacksWith === a;
}

// Legacy singular `placement` strings are resolved through the registry's
// placementAlias field — the old ['left-chest', …] literal is gone.
export function placementOptions(reg: DecorationRegistry | undefined): [string, string][] {
  return sortedLocations(reg)
    .filter((l) => !!l.placementAlias)
    .map((l) => [l.placementAlias as string, `${l.placementAlias} (${l.code} · ${l.label})`]);
}

export function codeForPlacement(reg: DecorationRegistry, placement: string | undefined): string {
  const hit = Object.values(reg?.locations || {}).find((l) => l.placementAlias === placement);
  if (!hit) throw new Error(`No decoration location for legacy placement "${placement}"`);
  return hit.code;
}

export function slugColor(s: string | undefined): string {
  return String(s || '').toLowerCase().trim()
    .replace(/[\s_/]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// Human label for the catalog list + the editor's static tiers line.
export function tierLabel(p: { tier?: number; tiers?: number[] }): string {
  const ts = [...new Set(
    Array.isArray(p.tiers) && p.tiers.length ? p.tiers : (p.tier ? [p.tier] : []),
  )].filter(isPrizeTier).sort((a, b) => a - b);
  if (!ts.length) return 'Tier —';
  if (ts.length === 1) return `Tier ${ts[0]}`;
  if (ts.length === MAX_PRIZE_TIERS && ts[0] === 1 && ts[ts.length - 1] === MAX_PRIZE_TIERS) {
    return `Tiers 1–${MAX_PRIZE_TIERS}`;
  }
  if (ts.length === 2 && ts[0] === 1 && ts[1] === 2) return 'Tier 1 & 2';
  return `Tiers ${ts.join(', ')}`;
}

// Order prize products by the console drag order; missing sortOrder sorts last.
export function bySortOrder(a: { id: string; sortOrder?: number }, b: { id: string; sortOrder?: number }): number {
  const oa = Number.isFinite(a.sortOrder) ? (a.sortOrder as number) : Number.MAX_SAFE_INTEGER;
  const ob = Number.isFinite(b.sortOrder) ? (b.sortOrder as number) : Number.MAX_SAFE_INTEGER;
  return oa - ob || String(a.id || '').localeCompare(String(b.id || ''));
}

// Read-time contract shared with productconfig/lib/decoration.js
// derivePrintAreas(): products WITHOUT printAreas are synthesized from the
// legacy singular placement/production/mockup fields so the editor shows the
// operator what the pipeline actually sees today. Throws exactly where the
// server throws (unknown placement, view with no base image).
export function derivePrintAreas(
  p: PrizeProduct,
  reg: DecorationRegistry,
): { printAreas: Record<string, PrintArea>; mockupViews: Record<string, MockupView> } {
  if (p.printAreas && Object.keys(p.printAreas).length) {
    for (const code of Object.keys(p.printAreas)) resolveLocation(reg, code);
    const mockupViews = p.mockupViews || {};
    assertMockupViews(p.printAreas, mockupViews);
    return { printAreas: p.printAreas, mockupViews };
  }
  const code = codeForPlacement(reg, p.placement);
  const m = p.mockup || ({} as PrizeProductMockup);
  const printAreas: Record<string, PrintArea> = {
    [code]: {
      widthInches: p.production?.width,
      heightInches: p.production?.height,
      mockup: {
        view: 'front', x: m.logoPosition?.x, y: m.logoPosition?.y,
        maxW: m.logoMaxWidth, maxH: m.logoMaxHeight,
      } as PrintAreaMockup,
    },
  };
  const mockupViews: Record<string, MockupView> = {
    front: { baseImage: m.baseImage, ...(m.overlayImage ? { overlayImage: m.overlayImage } : {}) },
  };
  assertMockupViews(printAreas, mockupViews);
  return { printAreas, mockupViews };
}

// F14: a declared mockup.view must resolve to a mockupViews entry carrying a
// non-empty baseImage, or the proof renderer composites onto nothing.
export function assertMockupViews(
  printAreas: Record<string, PrintArea>,
  mockupViews: Record<string, MockupView>,
): void {
  for (const [code, area] of Object.entries(printAreas)) {
    if (!area?.mockup) continue;
    const view = area.mockup.view;
    if (!view) throw new Error(`printAreas["${code}"]: mockup.view is required when a mockup block is present`);
    const entry = mockupViews?.[view];
    if (!entry) throw new Error(`printAreas["${code}"].mockup.view="${view}" has no mockupViews["${view}"] entry`);
    if (!entry.baseImage) throw new Error(`mockupViews["${view}"]: baseImage is required (referenced by printAreas["${code}"])`);
  }
}

// The codes that will become `[CODE]designId` SKU segments: every logo-capable
// print area, in registry sort order. Name/number areas carry no static design
// id — they are minted per player at order time.
export function skuCodes(p: PrizeProduct, reg: DecorationRegistry): string[] {
  const { printAreas } = derivePrintAreas(p, reg);
  return Object.keys(printAreas)
    .map((code) => resolveLocation(reg, code))
    .filter((loc) => loc.kinds.includes('logo'))
    .sort((a, b) => a.sort - b.sort)
    .map((loc) => loc.code);
}

// Cosmetic only; real SKUs are generated by productconfig at approval.
// Mirrors productconfig/lib/decoration.js generateMultiSlotSKU. Throws when the
// product's locations cannot be resolved — the caller surfaces the message
// instead of printing a made-up code.
export function previewSku(p: PrizeProduct, reg: DecorationRegistry, designId = 'XXXXX', size = 'M'): string {
  const codes = skuCodes(p, reg);
  if (!codes.length) throw new Error('No logo print area — the SKU would carry no decoration segment.');
  const deco = codes.map((code) => `[${code}]${designId}`).join('+');
  const style = (p.sku?.styleCode || 'GSF000').toUpperCase();
  const color = slugColor(p.sku?.colorCode) || 'unknown';
  return `SH-${deco}-${style}-${color}-${size}`;
}

// native px → displayed px (render the box on the scaled image)
export const nativeToBox = (v: number, scale: number) => v * scale;
// displayed px → native px (convert drag deltas / sizes back to stored coords)
export const boxToNative = (v: number, scale: number) => Math.round(v / scale);

export function clampBox(z: Zone, natW: number, natH: number): Zone {
  const w = Math.min(Math.max(20, Math.round(z.logoMaxWidth)), natW);
  const h = Math.min(Math.max(20, Math.round(z.logoMaxHeight)), natH);
  const x = Math.min(Math.max(0, Math.round(z.x)), natW - w);
  const y = Math.min(Math.max(0, Math.round(z.y)), natH - h);
  return { x, y, logoMaxWidth: w, logoMaxHeight: h };
}

// Mirror of the server's printAreas rules (../../../lib/prize-product.js
// validateDecoration) so typos surface before the round trip. The server is
// still the authority — this never lets anything through that it would reject.
function validatePrintAreas(p: PrizeProduct, reg: DecorationRegistry): string | null {
  const areas = p.printAreas || {};
  try {
    for (const [code, area] of Object.entries(areas)) {
      const loc = resolveLocation(reg, code);
      for (const key of ['widthInches', 'heightInches'] as const) {
        const v = area[key];
        if (!Number.isFinite(v) || (v as number) <= 0) return `${code} (${loc.label}): ${key} must be a number greater than 0.`;
      }
      const textKeys = (['font', 'maxChars', 'maxDigits', 'ink'] as const).filter((k) => area[k] !== undefined && area[k] !== '');
      if (textKeys.length && !isTextCapable(loc)) {
        return `${code} (${loc.label}) cannot carry a name or number, so ${textKeys.join(', ')} must be cleared.`;
      }
      if (area.font !== undefined && area.font !== '' && !isPrizeFont(area.font)) {
        return `${code} (${loc.label}): font must be one of ${PRIZE_FONT_IDS.join(', ')}.`;
      }
      if (area.ink !== undefined && area.ink !== '' && !/^#[0-9a-fA-F]{6}$/.test(area.ink)) {
        return `${code} (${loc.label}): ink must be a hex colour like #FFFFFF.`;
      }
      if (area.mockup) {
        for (const key of ['x', 'y', 'maxW', 'maxH'] as const) {
          const v = area.mockup[key];
          if (!Number.isFinite(v) || v < 0) return `${code} (${loc.label}): mockup ${key} must be a number of 0 or more.`;
        }
      }
    }
    assertMockupViews(areas, p.mockupViews || {});
  } catch (e) {
    return e instanceof Error ? e.message : 'Print areas are invalid.';
  }
  return null;
}

export function clientValidate(p: PrizeProduct, reg?: DecorationRegistry): string | null {
  if (!/^[a-z0-9][a-z0-9-]*$/.test(p.id || '')) return 'ID must be lowercase kebab-case (a-z, 0-9, hyphen).';
  if (!p.displayName?.trim()) return 'Display name is required.';
  const scalar = isPrizeTier(p.tier) ? p.tier : (isPrizeTier(p.product_index) ? p.product_index : null);
  if (Array.isArray(p.tiers)) {
    if (!p.tiers.length || !p.tiers.every(isPrizeTier)) {
      return `Tiers must be a non-empty list of 1–${MAX_PRIZE_TIERS}.`;
    }
  } else if (scalar == null) {
    return `Tier must be 1–${MAX_PRIZE_TIERS}, or provide tiers.`;
  }
  if (!p.shopify?.sizes?.length) return 'Add at least one size.';
  if (p.shopify?.basePrice && Number.isNaN(Number(p.shopify.basePrice))) return 'Base price must be a number.';
  if (!p.mockup?.baseImage) return 'Pick a base mockup image.';
  const lp = p.mockup?.logoPosition;
  if (!lp || lp.x < 0 || lp.y < 0) return 'Set the logo placement on the mockup.';
  if (!(p.mockup.logoMaxWidth > 0) || !(p.mockup.logoMaxHeight > 0)) return 'Logo box size must be positive.';
  const hasAreas = !!p.printAreas && Object.keys(p.printAreas).length > 0;
  if (!hasAreas && !p.placement) return 'Pick a placement (or add print areas).';
  if (hasAreas) {
    if (!reg) return 'Decoration locations are still loading — try again in a moment.';
    const areaProblem = validatePrintAreas(p, reg);
    if (areaProblem) return areaProblem;
  }
  return null;
}

export function blankPrizeProduct(): PrizeProduct {
  return {
    // placement stays empty: the dialog selects the first registry placement
    // alias once GET /api/decoration-locations lands, so no location string is
    // hardcoded on this side.
    id: '', displayName: '', shortName: '', tier: 1, tiers: prizeIndexes(MAX_PRIZE_TIERS), active: true,
    garment: '', color: '', placement: '',
    sku: { styleCode: '', colorCode: '' },
    mockup: { baseImage: '', overlayImage: '', logoPosition: { x: 0, y: 0 }, logoMaxWidth: 100, logoMaxHeight: 100 },
    production: { width: 3.2, height: 3.2, unit: 'inches', dpi: 300 },
    shopify: {
      titlePrefix: '', skuPrefix: '', skuSuffix: '', sizes: [], basePrice: '', compareAtPrice: '',
      productType: '', vendor: 'Ghostfit Sports Prizes', tags: [], trackInventory: true, taxable: false,
      publishedScope: 'web', bodyHtml: '',
    },
  };
}
